import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import type { Fee, RatePeriod } from '../data/model'
import { todayISO } from '../lib/dates'
import { hasSeasonalRates } from '../lib/pricePlan'
import { Badge, Button, Card, Field, Input, Select, Sheet, cx } from '../ui/kit'
import { PriceAdviceCard } from './PriceAdviceCard'
import { ColdStartPriceCard } from './PricePlanCard'

const DOW = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const DOW_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const ALL_DAYS = 127

/** The fee basis is a database enum; it must never reach the screen untranslated. */
const BASES: Fee['basis'][] = [
  'per_stay', 'per_night', 'per_person', 'per_person_night', 'percent']

const BASIS_LABEL: Record<'de' | 'en', Record<Fee['basis'], string>> = {
  de: {
    per_stay: 'pro Aufenthalt',
    per_night: 'pro Nacht',
    per_person: 'pro Person',
    per_person_night: 'pro Person/Nacht',
    percent: '% vom Übernachtungspreis',
  },
  en: {
    per_stay: 'per stay',
    per_night: 'per night',
    per_person: 'per person',
    per_person_night: 'per person/night',
    percent: '% of accommodation',
  },
}

function emptyRate(tenantId: string): RatePeriod {
  const year = todayISO().slice(0, 4)
  return {
    id: crypto.randomUUID(),
    tenant_id: tenantId,
    unit_id: null,
    name: '',
    valid_from: `${year}-01-01`,
    valid_to: `${year}-12-31`,
    dow_mask: ALL_DAYS,
    price_per_night: 0,
    extra_person_price: null,
    base_occupancy: null,
    min_nights: null,
    priority: 0,
    active: true,
    online_enquiry: true,
  }
}

function emptyFee(tenantId: string): Fee {
  return {
    id: crypto.randomUUID(),
    tenant_id: tenantId,
    unit_id: null,
    code: 'extra',
    name: { de: '', en: '' },
    basis: 'per_stay',
    amount: 0,
    vat_rate: 20,
    mandatory: true,
    taxable_age_from: null,
    max_nights: null,
    sort_order: 99,
    active: true,
  }
}

/**
 * Seasons and fees, editable.
 *
 * Price management is the headline of the product, so leaving these read-only made the
 * whole Settings screen a dead end. Seasons are ordered by priority because that is
 * what decides which one wins on a given night — showing them in any other order makes
 * an overlapping rate look like a bug.
 */
export function RatesCard() {
  const { t, formatMoney, lang, pick } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const days = de ? DOW : DOW_EN

  const [rate, setRate] = useState<RatePeriod | null>(null)
  const [fee, setFee] = useState<Fee | null>(null)

  const seasons = [...store.db.rate_periods].sort(
    (a, b) => b.priority - a.priority || a.valid_from.localeCompare(b.valid_from))

  /*
   * Der Kaltstart-Fall: außer dem Platzhalter aus der Einrichtung gibt es nichts.
   *
   * Bewusst nicht `seasons.length === 0` — die Einrichtung legt eine Ganzjahreszeile
   * an (`create_my_tenant`), die Liste ist also nie wirklich leer. Wer nur sie hat,
   * hat trotzdem noch keine Preise gemacht.
   */
  const coldStart = !hasSeasonalRates(store.db.rate_periods, todayISO())

  return (
    <>
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="font-medium flex-1">{t('nav.rates')}</h2>
          <Button size="sm" onClick={() => setRate(emptyRate(store.db.tenant.id))}>
            <Plus size={15} /> {de ? 'Saison' : 'Season'}
          </Button>
        </div>
        <p className="text-xs text-muted">
          {de
            ? 'Bei Überschneidung gewinnt die Saison mit der höchsten Priorität — deshalb stehen sie hier in dieser Reihenfolge.'
            : 'Where seasons overlap the highest priority wins, which is why they are listed in that order.'}
        </p>

        {seasons.length === 0 ? (
          /* Der leere Zustand zeigt auf die Karte direkt darunter. „Keine Ergebnisse"
             allein lässt den neuen Vermieter genau dort stehen, wo er nicht
             weiterweiß — und die Hilfe eine Bildschirmhöhe entfernt übersieht er. */
          <p className="text-sm text-muted">
            {coldStart
              ? (de
                  ? 'Noch keine Saison angelegt — gleich darunter hilft Ihnen die KI beim Anfang.'
                  : 'No season yet — the card just below helps you get started.')
              : t('common.noResults')}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {seasons.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => setRate(r)}
                  className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-surface-2 rounded-lg px-1"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">
                      {r.name || (de ? 'Ohne Namen' : 'Unnamed')}
                      {!r.active && <span className="text-muted"> · {de ? 'inaktiv' : 'inactive'}</span>}
                    </div>
                    <div className="text-xs text-muted truncate">
                      {r.valid_from.slice(5)} – {r.valid_to.slice(5)}
                      {r.dow_mask !== ALL_DAYS && ` · ${maskLabel(r.dow_mask, days)}`}
                      {r.unit_id && ` · ${store.unitById(r.unit_id)?.name ?? ''}`}
                      {r.min_nights
                        ? ` · ${de ? `ab ${r.min_nights} Nächten` : `min ${r.min_nights} nights`}`
                        : ''}
                      {/* Abbreviated: the full word truncates on a phone. */}
                      {r.priority !== 0 && ` · ${de ? 'Prio.' : 'prio.'} ${r.priority}`}
                    </div>
                  </div>
                  {r.online_enquiry === false && (
                    <Badge tone="warn">{de ? 'offline' : 'offline'}</Badge>
                  )}
                  <span className="tabular-nums font-medium whitespace-nowrap">
                    {formatMoney(r.price_per_night)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="border-t border-border pt-3 flex items-center gap-2">
          <h3 className="font-medium text-sm flex-1">{de ? 'Gebühren' : 'Fees'}</h3>
          <Button size="sm" onClick={() => setFee(emptyFee(store.db.tenant.id))}>
            <Plus size={15} /> {de ? 'Gebühr' : 'Fee'}
          </Button>
        </div>
        <ul className="divide-y divide-border">
          {store.db.fees.map((f) => (
            <li key={f.id}>
              <button
                onClick={() => setFee(f)}
                className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-surface-2 rounded-lg px-1"
              >
                <span className="min-w-0 flex-1">
                  <span className="text-sm truncate block">{pick(f.name) || f.code}</span>
                  <span className="text-xs text-muted">
                    {BASIS_LABEL[de ? 'de' : 'en'][f.basis]}
                    {!f.mandatory && ` · ${de ? 'zubuchbar' : 'optional'}`}
                  </span>
                </span>
                <span className="tabular-nums whitespace-nowrap">{formatMoney(f.amount)}</span>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {/* Die Kaltstart-Karte steht zwischen der Preisliste und den gerechneten
          Empfehlungen — direkt unter der leeren Liste, auf die sie antwortet, und
          über der Karte, die erst mit Buchungen etwas zu sagen hat. */}
      {coldStart && <ColdStartPriceCard />}

      <PriceAdviceCard />

      {/* Der Weg zurück, wenn der Streifen über dem Kalender weggeklickt wurde.
          Ohne ihn wäre das Kreuz dort eine Einbahnstraße. */}
      {store.db.tenant.price_advice_enabled === false && (
        <Card className="p-4 flex items-center gap-3">
          <p className="text-sm text-muted flex-1">
            {lang === 'de'
              ? 'Die Preisempfehlungen über dem Kalender sind ausgeschaltet.'
              : 'Price suggestions above the calendar are switched off.'}
          </p>
          <Button size="sm" variant="secondary"
            onClick={() => store.saveTenant({ price_advice_enabled: true })}>
            {lang === 'de' ? 'Wieder einschalten' : 'Switch back on'}
          </Button>
        </Card>
      )}

      <RateSheet rate={rate} onClose={() => setRate(null)} />
      <FeeSheet fee={fee} onClose={() => setFee(null)} />
    </>
  )
}

function maskLabel(mask: number, days: string[]): string {
  return days.filter((_, i) => (mask & (1 << i)) !== 0).join('')
}

function RateSheet({ rate, onClose }: { rate: RatePeriod | null; onClose: () => void }) {
  const { t, lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const days = de ? DOW : DOW_EN
  const [draft, setDraft] = useState<RatePeriod | null>(rate)

  useEffect(() => setDraft(rate), [rate])
  if (!draft) return null

  const set = (patch: Partial<RatePeriod>) => setDraft({ ...draft, ...patch })
  const exists = store.db.rate_periods.some((r) => r.id === draft.id)

  return (
    <Sheet
      open={!!rate}
      onClose={onClose}
      title={draft.name || (de ? 'Neue Saison' : 'New season')}
      footer={
        <>
          {exists && (
            <Button variant="danger"
              onClick={async () => { await store.deleteRatePeriod(draft.id); onClose() }}>
              <Trash2 size={16} />
            </Button>
          )}
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary" className="flex-1"
            disabled={!draft.name.trim() || draft.valid_to < draft.valid_from}
            onClick={async () => { await store.upsertRatePeriod(draft); onClose() }}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={de ? 'Bezeichnung' : 'Name'}>
        <Input autoFocus value={draft.name} placeholder={de ? 'Hochsaison Sommer' : 'High season'}
          onChange={(e) => set({ name: e.target.value })} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t('common.from')}>
          <Input type="date" value={draft.valid_from}
            onChange={(e) => set({ valid_from: e.target.value })} />
        </Field>
        <Field label={t('common.to')} hint={de ? 'einschließlich' : 'inclusive'}>
          <Input type="date" value={draft.valid_to} min={draft.valid_from}
            onChange={(e) => set({ valid_to: e.target.value })} />
        </Field>
      </div>

      <Field
        label={de ? 'Gilt an diesen Tagen' : 'Applies on these days'}
        hint={de ? 'Für Wochenendzuschläge nur Fr und Sa wählen.' : 'For a weekend surcharge pick Fri and Sat only.'}
      >
        <div className="flex gap-1.5">
          {days.map((d, i) => {
            const on = (draft.dow_mask & (1 << i)) !== 0
            return (
              <button
                key={d}
                onClick={() => set({ dow_mask: draft.dow_mask ^ (1 << i) })}
                className={cx('flex-1 py-2 rounded-lg text-xs font-medium border',
                  on ? 'bg-accent text-accent-fg border-accent'
                     : 'bg-surface-2 text-muted border-border')}
              >
                {d}
              </button>
            )
          })}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field
          label={`${t('booking.price')} / ${t('common.night')}`}
          hint={store.db.tenant.prices_include_vat === false
            ? (de ? 'zzgl. USt.' : 'plus VAT')
            : (de ? 'inkl. USt.' : 'incl. VAT')}
        >
          <Input type="number" step="0.01" min={0} value={draft.price_per_night}
            onChange={(e) => set({ price_per_night: Number(e.target.value) })} />
        </Field>
        {/* Der Zusatz ist keine Regel des Schemas, sondern die Konvention, nach der
            die Preisempfehlungen anlegen (SURCHARGE_PRIORITY_FLOOR in priceAdvice.ts).
            Sie steht hier, damit ein handgeschriebener Zuschlag im selben Band landet
            und eine später angelegte Saison ihn nicht klammheimlich überholt. */}
        <Field
          label={de ? 'Priorität' : 'Priority'}
          hint={de ? 'Höher gewinnt · Zuschläge ab 100' : 'Higher wins · surcharges from 100'}
        >
          <Input type="number" value={draft.priority}
            onChange={(e) => set({ priority: Number(e.target.value) })} />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label={de ? 'Mindestnächte' : 'Minimum nights'}>
          <Input type="number" min={1} value={draft.min_nights ?? ''}
            placeholder="—"
            onChange={(e) => set({
              min_nights: e.target.value === '' ? null : Number(e.target.value),
            })} />
        </Field>
        <Field label={de ? 'Zusatzperson / Nacht' : 'Extra person / night'}>
          <Input type="number" step="0.01" value={draft.extra_person_price ?? ''}
            placeholder="—"
            onChange={(e) => set({
              extra_person_price: e.target.value === '' ? null : Number(e.target.value),
            })} />
        </Field>
      </div>

      <Field
        label={t('booking.unit')}
        hint={de ? 'Leer = gilt für alle Einheiten' : 'Empty = applies to every unit'}
      >
        <Select value={draft.unit_id ?? ''}
          onChange={(e) => set({ unit_id: e.target.value || null })}>
          <option value="">{de ? 'Alle Einheiten' : 'All units'}</option>
          {store.db.units.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </Select>
      </Field>

      <label className="flex items-center gap-3 text-sm">
        <input type="checkbox" className="w-4 h-4 accent-[var(--color-accent)]"
          checked={draft.online_enquiry !== false}
          onChange={(e) => set({ online_enquiry: e.target.checked })} />
        <span>{de ? 'Online anfragbar' : 'Open to online enquiries'}</span>
      </label>
      <label className="flex items-center gap-3 text-sm">
        <input type="checkbox" className="w-4 h-4 accent-[var(--color-accent)]"
          checked={draft.active}
          onChange={(e) => set({ active: e.target.checked })} />
        <span>{de ? 'Aktiv' : 'Active'}</span>
      </label>
    </Sheet>
  )
}

function FeeSheet({ fee, onClose }: { fee: Fee | null; onClose: () => void }) {
  const { t, lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const [draft, setDraft] = useState<Fee | null>(fee)

  useEffect(() => setDraft(fee), [fee])
  if (!draft) return null

  const set = (patch: Partial<Fee>) => setDraft({ ...draft, ...patch })
  const exists = store.db.fees.some((f) => f.id === draft.id)

  return (
    <Sheet
      open={!!fee}
      onClose={onClose}
      title={draft.name?.[lang] || (de ? 'Neue Gebühr' : 'New fee')}
      footer={
        <>
          {exists && (
            <Button variant="danger"
              onClick={async () => { await store.deleteFee(draft.id); onClose() }}>
              <Trash2 size={16} />
            </Button>
          )}
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary" className="flex-1"
            disabled={!(draft.name?.de || draft.name?.en)}
            onClick={async () => { await store.saveFee(draft); onClose() }}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      {/* Both languages, because this label is printed on the guest's invoice. */}
      <Field label={de ? 'Bezeichnung (Deutsch)' : 'Name (German)'}>
        <Input autoFocus value={draft.name?.de ?? ''} placeholder="Endreinigung"
          onChange={(e) => set({ name: { ...draft.name, de: e.target.value } })} />
      </Field>
      <Field label={de ? 'Bezeichnung (Englisch)' : 'Name (English)'}>
        <Input value={draft.name?.en ?? ''} placeholder="Final cleaning"
          onChange={(e) => set({ name: { ...draft.name, en: e.target.value } })} />
      </Field>

      <Field label={de ? 'Berechnung' : 'Charged'}>
        <Select value={draft.basis}
          onChange={(e) => set({ basis: e.target.value as Fee['basis'] })}>
          {BASES.map((b) => (
            <option key={b} value={b}>{BASIS_LABEL[de ? 'de' : 'en'][b]}</option>
          ))}
        </Select>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={draft.basis === 'percent' ? '%' : '€'}>
          <Input type="number" step="0.01" min={0} value={draft.amount}
            onChange={(e) => set({ amount: Number(e.target.value) })} />
        </Field>
        <Field label={`${t('common.vat')} %`}>
          <Input type="number" step="0.1" min={0} value={draft.vat_rate}
            onChange={(e) => set({ vat_rate: Number(e.target.value) })} />
        </Field>
      </div>

      <Field
        label={t('booking.unit')}
        hint={de ? 'Leer = gilt für alle Einheiten' : 'Empty = applies to every unit'}
      >
        <Select value={draft.unit_id ?? ''}
          onChange={(e) => set({ unit_id: e.target.value || null })}>
          <option value="">{de ? 'Alle Einheiten' : 'All units'}</option>
          {store.db.units.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </Select>
      </Field>

      <label className="flex items-center gap-3 text-sm">
        <input type="checkbox" className="w-4 h-4 accent-[var(--color-accent)]"
          checked={draft.mandatory}
          onChange={(e) => set({ mandatory: e.target.checked })} />
        <span>
          {de ? 'Immer berechnen' : 'Always charged'}
          <span className="text-muted">
            {de ? ' — sonst pro Buchung auswählbar' : ' — otherwise selectable per booking'}
          </span>
        </span>
      </label>
      <label className="flex items-center gap-3 text-sm">
        <input type="checkbox" className="w-4 h-4 accent-[var(--color-accent)]"
          checked={draft.active}
          onChange={(e) => set({ active: e.target.checked })} />
        <span>{de ? 'Aktiv' : 'Active'}</span>
      </label>
    </Sheet>
  )
}
